import { useState, useCallback, useMemo } from 'react'
import { ITHookFunctionTy, ITLayoutTableModalOpen, ITComponentElement, OnChangeElements, ColumnITLayoutTableConfiguration, InputTableCustomFunctions } from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/types'
import { ColumnConfiguration } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { ITDeleteRow } from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import { TableWithoutDefaultRowRenderer } from '../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import { TableWithoutVirtualListingContainerRenderer, TableWithoutVirtualListingRowProps } from '../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing/types'
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, UniqueIdentifier, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { flushSync } from 'react-dom'
import { ITSortableItem } from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Sort'
import { twinFetchPostJSON } from '../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import { useOnChangeOrderTableLogic, useOrderTableRender } from '../useOrderTableHooks'
import { parseScaleOrderDataSingle } from '../../useOrderScreenLogic/functions'
import Checkbox from '../../../../../../forms/Checkbox'
import { useSelectOrderTableLogic } from '../TableComponents/useSelectOrderTableLogic'
import { getFilteredTableDataLength } from './functions'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import useInputTableFilterFnc from '../../../../../../utils/hooks/orders/useInputTableFilterFnc'

export const useOrderLayoutTableLogic: ITHookFunctionTy = ({ rowRenderer, components, columns, CurrencyId, setCompleteData, inputTableURLs, getCompleteData, completeData, customFunctions, myAddExtraProps, myAddExtraStyles, ...rest }) => {
    const [openModal, setOpenModal] = useState<ITLayoutTableModalOpen>(null)
    const [activeNChildren, setActiveNChildren] = useState<number | null>(null);
    const { onChangeSearchBar, changeSingleField } = useInputTableFilterFnc({ setCompleteData })
    const { onChangeTotals, onChangeFetch, onChangeTotalsFetch, onChange, onChangeAdd, onChangeUpdate, onChangeUpdateFetch, addNewProductLine, deleteLine, changeOrder } = useOnChangeOrderTableLogic({ setCompleteData, inputTableURLs, getCompleteData, completeData, ...rest })
    const { changeSelectedOneLine, selectAllLines, processedLength, selectedLength } = useSelectOrderTableLogic({ setCompleteData, completeData })
    const { addButtonRowRender, addCombinedHeader, addExtraProps, addOrderHeader } = useOrderTableRender({ completeData })
    const {t} = useTwinTranslation()
    const deliveryNoteText = t('deliveryNote', 'Albarán')
    const orderText = t('sellOrder', 'Pedido')
    const invoiceText = t('sellInvoice', 'Factura')
    const filteredTableDataLength = getFilteredTableDataLength(completeData)
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 50,
                tolerance: 50
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    )

    const handleProductLineTypes = useCallback((props: TableWithoutVirtualListingRowProps) => {
        if (props?.rowData?.type === 'addButton') {
            props.key = 'addButton'
            return addButtonRowRender(props, addNewProductLine)
        } else if (props?.rowData?.type === 'productCombinedHeader') {
            props.key = 'productCombinedHeader' + props.rowData.id + props.rowData.FirstChildId
            return addCombinedHeader(props, setOpenModal)
        } else if (props?.rowData?.type === 'productCombinedHeaderAddon') {
            if (props.rowData['Product.ProductVariantGroupId']) {
                props.className += ' row_productcombined_variant'
            }
            return <TableWithoutDefaultRowRenderer {...props} className={(props.className || '') + ' product_combined_addon_header'} key={'productCombinedHeaderAddon' + props.rowData.id + props.rowData.FirstChildId} />
        } else if (props?.rowData?.type === 'sellOrderHeader') {
            props.key = 'sellOrderHeader'+props.rowData.id
            return addOrderHeader(props, orderText)
        } else if (props?.rowData?.type === 'sellDeliveryNoteHeader') {
            props.key = 'sellDeliveryNoteHeader'+props.rowData.id
            return addOrderHeader(props, deliveryNoteText)
        } else if (props?.rowData?.type === 'sellInvoiceHeader') {
            props.key = 'sellInvoiceHeader' + props.rowData.id
            return addOrderHeader(props, invoiceText)
        } 
        const { className } = myAddExtraProps?.(props) || addExtraProps(props)
        myAddExtraStyles?.(props)

        return (
            <TableWithoutDefaultRowRenderer {...props} className={className} key={props.rowData?.id || props.rowIndex} />
        )
    }, [addButtonRowRender, addCombinedHeader, addExtraProps, myAddExtraProps, addNewProductLine, setOpenModal, addOrderHeader, deliveryNoteText, orderText, invoiceText, myAddExtraStyles])

    const handleOnDragEnd = useCallback(async (event: DragEndEvent) => {
        const { active, over } = event
        if (over && active.id !== over.id) {
            let orderProducts: TwinDictionary[] = []
            flushSync(async () => {
                await setCompleteData((old) => {
                    if (!old) {
                        return null
                    }
                    let copy = JSON.parse(JSON.stringify(old))
                    const items = copy.tableData
                    const activeIndex = active.data?.current?.myIndex
                    const overIndex = over.data?.current?.myIndex
                    const myMoveItems = items.slice(activeIndex, activeIndex + (activeNChildren || 0) + 1)
                    items.splice(activeIndex, myMoveItems.length)
                    let myOverIndex = overIndex
                    if (overIndex > activeIndex) {
                        if (over.data?.current?.lastPosition) {
                            myOverIndex = over.data?.current?.lastPosition - myMoveItems.length + 1
                        } else {
                            myOverIndex = overIndex - myMoveItems.length + 1
                        }
                    }
                    items.splice(myOverIndex, 0, ...myMoveItems)
                    let nLine = 1
                    let inventPosition = 0
                    copy.scaleByProduct = {}
                    copy.scaleByVariable = {}
                    copy.dictPosTableData = {}
                    for (const key in items) {
                        const element  = items[key]
                        element.absoluteIndex = inventPosition
                        if (element.OrderId && element.id) {
                            if (element.nLine !== nLine) {
                                element.nLine = nLine
                                orderProducts.push({ nLine: element.nLine, id: element.id, OrderId: element.OrderId, ProductId: element.ProductId, taxUsed: element.taxUsed })
                            }
                            nLine++
                        }
                        parseScaleOrderDataSingle(copy, element, parseInt(key))
                        element.inventPosition = inventPosition
                        copy.dictPosTableData[element.id] = inventPosition
                        inventPosition++
                    }
                    copy.tableData = items
                    return copy
                })
                setActiveNChildren(null)
                await twinFetchPostJSON(inputTableURLs.updateOrderLines, { orderProducts })
            })
        }
    }, [setCompleteData, activeNChildren, inputTableURLs.updateOrderLines])

    const containerRenderer: TableWithoutVirtualListingContainerRenderer = useCallback((props) => {
        const result: JSX.Element[] = []
        let firstGroup: { render: JSX.Element[], ids: { id: UniqueIdentifier }[], parentId: number, firstPosition: number, type: 'addon' | 'variant', totalSum: number, firstTop: number } | undefined
        let secondGroup: { render: JSX.Element[], ids: { id: UniqueIdentifier }[], parentId: number, firstPosition: number } | undefined
        const resultIds: any[] = []
        for (const key in props.children) {
            const element = props.children[key]
            const dataElement = element?.props?.rowData
            const nextElement = props.children?.[(parseInt(key) + 1)]
            const dataNextElement = nextElement?.props?.rowData
            if (dataElement?.type && dataElement?.type !== 'addButton') {
                if (!firstGroup) {
                    firstGroup = {
                        render: [],
                        ids: [],
                        parentId: dataElement?.OrderVariantGroupId || dataElement?.id,
                        type: dataElement?.OrderVariantGroupId ? 'variant' : 'addon',
                        firstPosition: dataElement?.inventPosition,
                        totalSum: 0,
                        firstTop: element.props.style.top
                    }
                } else {
                    secondGroup = {
                        render: [],
                        ids: [],
                        parentId: dataElement?.id,
                        firstPosition: dataElement?.inventPosition
                    }
                }
            }
            if (secondGroup) {
                element.props.style.top = undefined
                element.props.style.position = undefined
                if (dataElement?.type !== 'productCombinedHeaderAddon') {
                    secondGroup.render.push(<ITSortableItem id={dataElement?.id} key={dataElement?.id} data={{ nChildren: 0, myIndex: dataElement?.inventPosition }} className='flex-col' style={element?.props?.style || {}}>{element}</ITSortableItem>)
                    secondGroup.ids.push(element.props.rowData?.id)
                } else {
                    secondGroup.render.push(element)
                }
            } else if (firstGroup) {
                element.props.style.top = undefined
                element.props.style.position = undefined
                if ((dataElement?.id && dataElement?.OrderId) && ((firstGroup.type === 'addon' && dataElement.OrderProductId) || (firstGroup.type === 'variant' && dataElement.OrderVariantGroupId))) {
                    firstGroup.render.push(<ITSortableItem id={dataElement?.id} key={dataElement?.id} data={{ nChildren: 0, myIndex: dataElement?.inventPosition }} className='flex-col'>{element}</ITSortableItem>)
                    firstGroup.ids.push(element.props.rowData?.id)
                } else {
                    firstGroup.render.push(element)
                }
            } else {
                if (!dataElement?.OrderId || !dataElement?.id) {
                    result.push(element)
                } else {
                    let top = element.props.style.top
                    element.props.style.top = undefined
                    element.props.style.position = undefined
                    result.push(<ITSortableItem id={dataElement?.id} key={dataElement?.id} data={{ nChildren: 0, myIndex: dataElement?.inventPosition }} className='flex-col'  style = {{ ...element?.props?.style || {}, top, position: 'absolute' }}>{element}</ITSortableItem>)
                    resultIds.push({ id: dataElement?.id })
                }
            }
            if (secondGroup && dataNextElement?.OrderProductId !== secondGroup.parentId) {
                const parsedId = "VariantWithAddon-" + firstGroup?.parentId + '-' + secondGroup.parentId
                firstGroup?.ids.push({ id: parsedId })
                const copySecondGroup = JSON.parse(JSON.stringify(secondGroup?.ids || []))?.length
                if (firstGroup) {
                    firstGroup.totalSum += copySecondGroup
                }
                firstGroup?.render.push(
                    <ITSortableItem id={parsedId} key={parsedId} className='flex-col' style={element?.props?.style || {}} data={{ nChildren: copySecondGroup, myIndex: secondGroup.firstPosition, lastPosition: dataElement?.inventPosition }}>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleOnDragEnd}>
                            <SortableContext strategy={verticalListSortingStrategy} items={secondGroup.ids}>
                                {secondGroup.render}
                            </SortableContext>
                        </DndContext>
                    </ITSortableItem>)
                secondGroup = undefined
            }
            if (firstGroup && ((firstGroup.type === 'addon' && dataNextElement?.OrderProductId !== firstGroup.parentId) || (firstGroup.type === 'variant' && dataNextElement?.OrderVariantGroupId !== firstGroup.parentId))) {
                const parsedId = "Group" + firstGroup.type + '-' + (firstGroup.parentId)
                resultIds?.push({
                    id: parsedId
                })
                const copyFirstGroup = JSON.parse(JSON.stringify(firstGroup.ids))?.length
                result?.push(
                    <ITSortableItem id={parsedId} key={parsedId} className='flex-col' data={{ nChildren: copyFirstGroup + firstGroup.totalSum, myIndex: firstGroup?.firstPosition, lastPosition: dataElement?.inventPosition }} style={{ ...element?.props?.style || {}, top: firstGroup.firstTop, position: 'absolute' }} >
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleOnDragEnd} onDragStart={({ active }) => { setActiveNChildren(active?.data?.current?.nChildren) }} onDragCancel={() => { setActiveNChildren(null) }}>
                            <SortableContext strategy={verticalListSortingStrategy} items={firstGroup.ids}>
                                {firstGroup.render}
                            </SortableContext>
                        </DndContext>
                    </ITSortableItem>)
                firstGroup = undefined
            }
        }
        return (
            <DndContext sensors={sensors} collisionDetection={closestCenter}
                onDragEnd={handleOnDragEnd} onDragStart={({ active }) => {
                    setActiveNChildren(active?.data?.current?.nChildren)
                }} onDragCancel={() => {
                    setActiveNChildren(null);
                }}>
                <SortableContext strategy={verticalListSortingStrategy} items={resultIds}>
                    <div className='data_rows h-full' style={props.style}>{result}</div>
                </SortableContext>
            </DndContext>
        )
    }, [sensors, handleOnDragEnd, setActiveNChildren])

    const addCustomRender = useCallback((columns: ITComponentElement<any>[], customFunctions?: InputTableCustomFunctions) => {
        const onChanges: OnChangeElements = {
            onChange: customFunctions?.onChange || onChange,
            onChangeFetch: customFunctions?.onChangeFetch || onChangeFetch,
            onChangeAdd: customFunctions?.onChangeAdd || onChangeAdd,
            onChangeTotals: customFunctions?.onChangeTotals || onChangeTotals,
            onChangeTotalsFetch: customFunctions?.onChangeTotalsFetch || onChangeTotalsFetch,
            onChangeUpdateFetch: customFunctions?.onChangeUpdateFetch || onChangeUpdateFetch,
        }
        let result: ColumnConfiguration[] = []
        if (components) {
            let findFirstNameColumn = false
            for (const key in columns) {
                let column = columns[key]
                const myElement = column as ColumnConfiguration
                if (column.component) {
                    const Element = components[column.component]
                    const onChange = onChanges[column?.typeOnChange || 'onChangeFetch']
                    if (column.component === 'ITDeleteRow') {
                        let MyDeleteComponent = ITDeleteRow
                        if (components['DeleteComponent']) {
                            MyDeleteComponent = components['DeleteComponent']
                        }
                        myElement.customRender = (parameterValue: any, allRowData: any) => <MyDeleteComponent key={'delete' + parameterValue} {...{ parameterValue, allRowData }}  onClick={(id, allRowData) => id ? setOpenModal({ type: 'deleteLine', allRowData }) : deleteLine(allRowData.id)} />
                    } else if (column.component === 'ITCheckbox'){
                        const originalLength = completeData?.originalTableData?.length || 0
                        const myTableDataLength = ((filteredTableDataLength ? filteredTableDataLength : originalLength) || 0) - (processedLength || 0)
                        const isChecked = selectedLength > 0 && selectedLength === myTableDataLength
                        const disabled = column?.extraProps?.disabled || false
                        const nextStepNotFinished = column?.extraProps?.nextStepNotFinished || false
                        const readOnlyFnc = column?.extraProps?.readOnlyFnc || false
                        column.dataKey = isChecked ? 'selected_full' : 'selected_not_full'
                        myElement.label = () => <Checkbox label={''} onChange={selectAllLines} checked={isChecked} disabled={disabled} />
                        let MyCheckboxComponent = components['CheckboxComponent']
                        myElement.customRender = (parameterValue: any, allRowData: any) => <MyCheckboxComponent key={'select' + parameterValue} {...{ parameterValue, allRowData, nextStepNotFinished, readOnlyFnc }} value={allRowData?.selected === true} onChange={(checked: boolean) => changeSelectedOneLine?.(checked, allRowData?.absoluteIndex || allRowData?.rowIndex)} />
                    }else {
                        let extraData: TwinDictionary = {}
                        if ((myElement.id === 'name' || myElement.id === 'referenceAndName' || myElement.id === 'reference' || myElement.id === 'code_bar') && !findFirstNameColumn) {
                            extraData.findFirstNameColumn = true
                            findFirstNameColumn = true
                        }
                        myElement.customRender = (value, allRowData) => <Element {...column as any} {...{ value, allRowData, onChange, CurrencyId, setOpenModal, setCompleteData, ...extraData }} />
                    }
                }
                result.push(myElement)
            }
        }
        return result

    }, [CurrencyId, onChange, onChangeFetch, onChangeAdd, onChangeTotals, onChangeTotalsFetch, onChangeUpdateFetch, setOpenModal, deleteLine, components, changeSelectedOneLine, processedLength, selectAllLines, selectedLength, completeData?.originalTableData?.length, filteredTableDataLength, setCompleteData])

    const parseColumns = useCallback((columns: ColumnITLayoutTableConfiguration[]) => {
        let result = addCustomRender(columns as any, customFunctions)
        return result
    }, [addCustomRender, customFunctions])

    const parsedColumns = useMemo(() => parseColumns(columns), [columns, parseColumns])

    return { parsedColumns, rowRenderer: rowRenderer || handleProductLineTypes, setOpenModal, openModal, eventFunctions: { deleteLine, onChangeAdd, onChangeUpdate, changeOrder, changeSingleField, onChange, onChangeFetch }, containerRenderer, onChangeSearchBar, filters: completeData.filters, addCustomRender }
}